import { useMemo, useState } from 'react';
import JSZip from 'jszip';
import type { OutputGroup } from '../core/types';
import type { RunState } from '../App';

interface Props {
  run: RunState;
  onBack: () => void;
}

const TYPE_LABEL: Record<OutputGroup['type'], string> = {
  single_item_single_piece: '单品单件汇总',
  single_item_multi_piece: '单品多件',
  multi_item_mixed: '多品混合',
  po_group: 'PO 分组',
  tracking_group: 'Tracking 分组',
};

const STATUS_LABEL: Record<OutputGroup['status'], string> = {
  success: '成功',
  needs_review: '需复核',
  failed: '失败',
};

export default function ResultsPage({ run, onBack }: Props) {
  const { job, sourceFile } = run;
  const pipeline = run.pipeline;
  if (!pipeline) return null;
  const [filter, setFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [includeUnresolved, setIncludeUnresolved] = useState(false);
  const [zipBusy, setZipBusy] = useState(false);

  const summary = pipeline.summary;

  const filtered = useMemo(() => {
    const q = search.trim().toUpperCase();
    return pipeline.outputs.filter((o) => {
      if (filter !== 'all' && o.status !== filter) return false;
      if (!q) return true;
      return (
        o.fileName.toUpperCase().includes(q) ||
        (o.trackingNumber ?? '').includes(q) ||
        o.orderNumbers.some((po) => po.includes(q))
      );
    });
  }, [pipeline.outputs, filter, search]);

  const downloadFile = (fileName: string) => {
    const bytes = pipeline.files.get(fileName);
    if (!bytes) return;
    const blob = new Blob([bytes as unknown as BlobPart], { type: 'application/pdf' });
    triggerDownload(blob, fileName);
  };

  const preview = (fileName: string) => {
    const bytes = pipeline.files.get(fileName);
    if (!bytes) return;
    const blob = new Blob([bytes as unknown as BlobPart], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  };

  const downloadZip = async () => {
    setZipBusy(true);
    try {
      const zip = new JSZip();
      for (const [fileName, bytes] of pipeline.files) {
        if (fileName === 'unresolved-pages.pdf' && !includeUnresolved) continue;
        zip.file(fileName, bytes);
      }
      const blob = await zip.generateAsync({ type: 'blob' });
      triggerDownload(blob, `${summary.sourceFileName.replace(/\.pdf$/i, '')}_split.zip`);
    } finally {
      setZipBusy(false);
    }
  };

  const downloadReport = (kind: 'csv' | 'json') => {
    const content = kind === 'csv' ? pipeline.csvReport : pipeline.jsonReport;
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    triggerDownload(
      blob,
      `${summary.sourceFileName.replace(/\.pdf$/i, '')}_report.${kind}`,
    );
  };

  const stats: Array<[string, string | number]> = [
    ['源文件', sourceFile.name],
    ['总页数', summary.sourcePageCount],
    ['成功处理页', summary.processedPageCount],
    ['异常 / 待复核页', summary.needsReviewCount + summary.unresolvedPageCount],
    ['生成 PDF 数量', summary.outputFileCount],
    ['单品单件汇总页', summary.singlePageCount],
    ['PO 分组文件', summary.poGroupCount],
    ['处理耗时', `${Math.floor(summary.elapsedMs / 1000)} 秒`],
    ['Tracking 识别率', `${(summary.trackingRecognitionRate * 100).toFixed(1)}%`],
  ];

  return (
    <div className="page">
      <header className="hero compact">
        <h1>拆分完成</h1>
        <p>
          {summary.pageConservationOk
            ? '页面守恒校验通过：无丢页、无重复。'
            : '⚠ 页面守恒校验失败，请勿使用生成结果。'}
        </p>
      </header>

      <section className="stats-grid">
        {stats.map(([label, value]) => (
          <div className="stat" key={label}>
            <div className="stat-value">{value}</div>
            <div className="stat-label">{label}</div>
          </div>
        ))}
      </section>

      {summary.warnings.length > 0 && (
        <div className="alert warn">
          {summary.warnings.map((w) => (
            <div key={w}>• {w}</div>
          ))}
        </div>
      )}

      <div className="toolbar">
        <button className="primary" onClick={() => void downloadZip()} disabled={zipBusy}>
          {zipBusy ? '打包中…' : '下载全部 ZIP'}
        </button>
        <button className="secondary" onClick={() => downloadReport('csv')}>
          下载报告 CSV
        </button>
        <button className="secondary" onClick={() => downloadReport('json')}>
          下载报告 JSON
        </button>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={includeUnresolved}
            onChange={(e) => setIncludeUnresolved(e.target.checked)}
          />
          ZIP 中包含 unresolved-pages.pdf
        </label>
        <button className="ghost right" onClick={onBack}>
          ← 处理另一份文件
        </button>
      </div>

      <section className="filters">
        <select value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="all">全部状态</option>
          <option value="success">成功</option>
          <option value="needs_review">需复核</option>
          <option value="failed">失败</option>
        </select>
        <input
          type="search"
          placeholder="搜索文件名 / Tracking / PO…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </section>

      <table className="result-table">
        <thead>
          <tr>
            <th>输出文件名</th>
            <th>类型</th>
            <th>Tracking Number</th>
            <th>PO Number</th>
            <th>页数</th>
            <th>状态</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((o) => (
            <tr key={o.fileName}>
              <td className="mono">{o.fileName}</td>
              <td>{TYPE_LABEL[o.type]}</td>
              <td className="mono">{o.trackingNumber ?? (o.fileName === 'unresolved-pages.pdf' ? '—' : '多个')}</td>
              <td className="mono">{o.orderNumbers.length > 0 ? o.orderNumbers.join(', ') : '—'}</td>
              <td>{o.pageNumbers.length}</td>
              <td>
                <span className={`badge ${o.status}`}>{STATUS_LABEL[o.status]}</span>
                {o.warnings.length > 0 && (
                  <div className="cell-warnings" title={o.warnings.join('\n')}>
                    {o.warnings[0]}
                  </div>
                )}
              </td>
              <td className="actions-cell">
                <button className="link-btn" onClick={() => preview(o.fileName)}>
                  预览
                </button>
                <button className="link-btn" onClick={() => downloadFile(o.fileName)}>
                  下载
                </button>
              </td>
            </tr>
          ))}
          {filtered.length === 0 && (
            <tr>
              <td colSpan={7} className="empty">
                没有符合条件的输出文件
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {pipeline.unresolvedPages.length > 0 && (
        <section className="unresolved">
          <h2>无法识别的页面（{pipeline.unresolvedPages.length} 页）</h2>
          <p>
            原始页码：{pipeline.unresolvedPages.join(', ')}。这些页面已保留到{' '}
            <code>unresolved-pages.pdf</code>，请对照源 PDF 人工处理。
          </p>
          {pipeline.unresolvedPdf && (
            <button className="secondary" onClick={() => downloadFile('unresolved-pages.pdf')}>
              下载 unresolved-pages.pdf
            </button>
          )}
        </section>
      )}

      <p className="muted">{job ? `Job ID：${job.jobId}` : '本地处理模式，文件未上传'}</p>
    </div>
  );
}

function triggerDownload(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
